import {
  formatIsoDate,
  formatName,
  renderStatus,
} from "../../../utils/generalUtils";

import GeneratePdf from "../../../generatePdf/GeneratePdf";
import { Button, Space, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import ViewingFormButton from "../../Form/ViewingFormButton";
// import ConfirmDelete from "../../ConfirmDelete/ConfirmDelete";

// Required/Additional Details shown both in the pre-submit review and the
// post-submission preview.
const baseViewingDataConfig = [
  {
    key: 1,
    collapseName: "Required Details",
    collapseItems: [
      {
        key: 1,
        name: "submitter_position",
        label: "Position",
      },
      {
        key: 2,
        name: "start_date",
        label: "Start Date",
      },
      {
        key: 3,
        name: "end_date",
        label: "End Date",
      },
      {
        key: 4,
        name: "absent",
        label: "Leave of Absence",
      },
    ],
  },
  {
    key: 2,
    collapseName: "Additional Details",
    collapseItems: [
      {
        key: 1,
        name: "act1",
        label: "Activity 1",
      },
      {
        key: 2,
        name: "week1_hours",
        label: "No. of hours per week 1",
      },
      {
        key: 3,
        name: "act2",
        label: "Activity 2",
        hideIfEmpty: true,
      },
      {
        key: 4,
        name: "week2_hours",
        label: "No. of hours per week 2",
        hideIfEmpty: true,
      },
      {
        key: 5,
        name: "act3",
        label: "Activity 3",
        hideIfEmpty: true,
      },
      {
        key: 6,
        name: "week3_hours",
        label: "No. of hours per week 3",
        hideIfEmpty: true,
      },
      {
        key: 7,
        name: "act4",
        label: "Activity 4",
        hideIfEmpty: true,
      },
      {
        key: 8,
        name: "week4_hours",
        label: "No. of hours per week 4",
        hideIfEmpty: true,
      },
      {
        key: 9,
        name: "act5",
        label: "Activity 5",
        hideIfEmpty: true,
      },
      {
        key: 10,
        name: "week5_hours",
        label: "No. of hours per week 5",
        hideIfEmpty: true,
      },
    ],
  },
];

export const cosConfig = {
  docType: "Certificate of Service",
  title: "Certificate of Service",
  description:
    "Submit certificate of service. Your request will be reviewed by your assigned approver. Once approved, you may download the official document.",
  popupWidth: 700,
  apiConfig: {
    get: "/employee/cos",
    post: "/employee/addcos",
    update: "/employee/updatecos",
    cancel: "/employee/cancel/cos",
  },
  dataMapper: (item, index) => ({
    ...item,
    name: `${item?.submission?.submitter?.last_name || ""}, ${
      item?.submission?.submitter?.first_name || ""
    }`.trim(),
    formattedName: formatName(
      item?.submission?.submitter?.first_name,
      item?.submission?.submitter?.middle_name,
      item?.submission?.submitter?.last_name,
      "lfmcut"
    ),
    approver_name: `${item?.submission?.approval?.[0]?.approver?.last_name || ""} ${
      item?.submission?.approval?.[0]?.approver?.first_name || ""
    }`.trim(),
    formattedApproverName: formatName(
      item?.submission?.approval?.[0]?.approver?.first_name,
      item?.submission?.approval?.[0]?.approver?.middle_name,
      item?.submission?.approval?.[0]?.approver?.last_name,
      "lfmcut"
    ),
    submitter_position: item?.submission?.position?.position_name,
    submitter_position_unit: item?.submission?.position?.unit,
    approval_date: item?.submission?.approval?.[0]?.approval_date,
    remarks: item?.submission?.remarks,
    date_submitted: item?.created_at ? formatIsoDate(item?.created_at) : null,
    key: index + 1,
    status: item?.submission?.status,
  }),
  // Used for the pre-submit review modal (PvForm) — no remarks yet at this point.
  viewingDataConfig: baseViewingDataConfig,
  // Used for the post-submission preview (ViewingFormButton) — adds the approver's remarks.
  postSubmissionViewingDataConfig: [
    ...baseViewingDataConfig,
    {
      key: 3,
      collapseName: "Remarks",
      collapseItems: [
        {
          key: 1,
          name: "remarks",
          label: "Remarks",
          render: (val) => val || "None",
        },
      ],
    },
  ],
  columns: (onDeleted, onEdit) => [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    {
      title: "Position",
      dataIndex: "submitter_position",
      key: "submitter_position",
    },
    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
        ["cancelled"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <Space>
          <GeneratePdf
            docuType="cos"
            contents={item}
            isEmployee={true}
            isIcon={true}
            disabled={item?.status !== "approved"}
          />
          <ViewingFormButton
            contents={item}
            viewingDataConfig={cosConfig.postSubmissionViewingDataConfig}
            isIcon={true}
          />
          <Tooltip
            title={
              item?.status === "pending"
                ? "Edit or cancel this submission"
                : "Only pending submissions can be edited"
            }
          >
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              disabled={item?.status !== "pending"}
              onClick={() => onEdit?.(item)}
              style={
                item?.status === "pending"
                  ? {
                      color: "white",
                      backgroundColor: "#FA8C16",
                      borderColor: "#FA8C16",
                    }
                  : {}
              }
            />
          </Tooltip>
          {/* <ConfirmDelete
            isIcon={true}
            rowId={item?.id}
            buttonText="Delete Submission"
            docType="cos"
            onDeleted={onDeleted}
          /> */}
        </Space>
      ),
    },
    {
      title: "Date Submitted",
      dataIndex: "date_submitted",
      key: "date_submitted",
    },
  ],
  // Reverse of the form -> payload mapping — maps a stored submission back
  // into edit-form values.
  toFormValues: (item) => ({
    date:
      item?.start_date && item?.end_date
        ? [dayjs(item.start_date), dayjs(item.end_date)]
        : undefined,
    start_date: item?.start_date,
    end_date: item?.end_date,
    absent_toggle: item?.absent ? "enable" : "disable",
    absent: item?.absent || "",
    act1: item?.act1,
    week1_hours: item?.week1_hours,
    act2: item?.act2,
    week2_hours: item?.week2_hours,
    act3: item?.act3,
    week3_hours: item?.week3_hours,
    act4: item?.act4,
    week4_hours: item?.week4_hours,
    act5: item?.act5,
    week5_hours: item?.week5_hours,
  }),
  formFields: [
    {
      name: "date",
      label: "Date",
      type: "daterange",
      onSameMonth: true,
      rules: [{ required: true, message: "This is required" }],
    },
    {
      name: "absent",
      label: "Date of Absences",
      // rules: [{ required: true }],
      extra:
        "Format: 'March 1, 2025, March 3, 2025' or 'March 1, 2025 to March 15, 2025'",
      type: "radiotextbox",
    },
    {
      name: "activities",
      type: "activity-hours-list",
    },
  ],
};
